import React, { useMemo, useState } from 'react';
import type { GuidelineDraftMetadata } from '../../types/accuracy';
import type { UnfixableDomainRule } from '../../types/guideline';
import UnfixableRulesSection from './UnfixableRulesSection';
import {
  DEFAULT_CHUNK_SETTINGS,
  listToMultiline,
  parseMultilineInput,
  sortUnfixableRules,
} from '../../services/guidelineDraftMetadata';

function useMetadataTexts(metadata: GuidelineDraftMetadata) {
  return {
    aspOptionsText: useMemo(() => listToMultiline(metadata.related_asps), [metadata.related_asps]),
    operationCompanyOptionsText: useMemo(
      () => listToMultiline(metadata.operation_companies),
      [metadata.operation_companies]
    ),
    mediaOptionsText: useMemo(() => listToMultiline(metadata.media_names), [metadata.media_names]),
    keywordListText: useMemo(() => listToMultiline(metadata.keyword_list), [metadata.keyword_list]),
    searchKeywordsText: useMemo(
      () => listToMultiline(metadata.search_keywords),
      [metadata.search_keywords]
    ),
    titleExtractionKeywordsText: useMemo(
      () => listToMultiline(metadata.other_content_keywords),
      [metadata.other_content_keywords]
    ),
    autoExpandKeywordsText: useMemo(
      () => listToMultiline(metadata.auto_expand_keywords),
      [metadata.auto_expand_keywords]
    ),
  };
}

export function GuidelineOrganizationSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const { aspOptionsText, operationCompanyOptionsText, mediaOptionsText } = useMetadataTexts(metadata);
  const patch = (next: Partial<GuidelineDraftMetadata>) => onChange({ ...metadata, ...next });

  return (
    <div className="organization-section">
      <h3 className="section-subtitle">関連組織リスト</h3>
      <div className="option-grid">
        <div className="option-card">
          <label className="option-label">関連ASP</label>
          <textarea
            className="option-textarea"
            placeholder="一行に1つのASPを入力"
            value={aspOptionsText}
            onChange={(e) => patch({ related_asps: parseMultilineInput(e.target.value) })}
          />
        </div>
        <div className="option-card">
          <label className="option-label">運営会社</label>
          <textarea
            className="option-textarea"
            placeholder="一行に1つの運営会社を入力"
            value={operationCompanyOptionsText}
            onChange={(e) => patch({ operation_companies: parseMultilineInput(e.target.value) })}
          />
        </div>
        <div className="option-card">
          <label className="option-label">媒体名</label>
          <textarea
            className="option-textarea"
            placeholder="一行に1つの媒体名を入力"
            value={mediaOptionsText}
            onChange={(e) => patch({ media_names: parseMultilineInput(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}

export function GuidelineChunkSettingsSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const cs = metadata.chunk_settings ?? DEFAULT_CHUNK_SETTINGS;
  const { keywordListText } = useMetadataTexts(metadata);

  const patchChunk = (field: keyof typeof DEFAULT_CHUNK_SETTINGS, value: number) => {
    onChange({
      ...metadata,
      chunk_settings: { ...cs, [field]: value },
    });
  };

  return (
    <div className="chunk-settings-section">
      <h3 className="section-subtitle">ページチャンク設定</h3>
      <div className="chunk-settings-grid">
        {(
          [
            ['chunk_context_before', 'CHUNK_CONTEXT_BEFORE', cs.chunk_context_before],
            ['chunk_context_after', 'CHUNK_CONTEXT_AFTER', cs.chunk_context_after],
            ['max_chunk_size', 'MAX_CHUNK_SIZE', cs.max_chunk_size],
            ['chunk_overlap', 'CHUNK_OVERLAP', cs.chunk_overlap],
            ['keyword_group_distance', 'KEYWORD_GROUP_DISTANCE', cs.keyword_group_distance],
          ] as const
        ).map(([field, label, value]) => (
          <div key={field} className="chunk-input">
            <label>{label}</label>
            <input
              type="number"
              value={value ?? 0}
              onChange={(e) => patchChunk(field, Number(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
      <div className="option-card keyword-card">
        <label className="option-label">キーワードリスト</label>
        <textarea
          className="option-textarea"
          placeholder="一行に1つのキーワードを入力"
          value={keywordListText}
          onChange={(e) =>
            onChange({ ...metadata, keyword_list: parseMultilineInput(e.target.value) })
          }
        />
      </div>
    </div>
  );
}

export function GuidelineSearchSettingsSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const { searchKeywordsText } = useMetadataTexts(metadata);

  return (
    <div className="category-section">
      <div className="category-header">
        <h3 className="section-subtitle">検索設定</h3>
      </div>
      <div className="search-settings-content">
        <div className="search-toggle-section">
          <label className="search-toggle-label">
            <input
              type="checkbox"
              checked={metadata.enable_search ?? false}
              onChange={(e) => onChange({ ...metadata, enable_search: e.target.checked })}
            />
            <span>検索を有効化</span>
          </label>
        </div>
        {metadata.enable_search && (
          <div className="option-card search-keywords-card">
            <label className="option-label">検索キーワード</label>
            <textarea
              className="option-textarea"
              placeholder="一行に1つの検索キーワードを入力"
              value={searchKeywordsText}
              onChange={(e) =>
                onChange({ ...metadata, search_keywords: parseMultilineInput(e.target.value) })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function GuidelineFilterSettingsSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const { titleExtractionKeywordsText } = useMetadataTexts(metadata);

  return (
    <div className="category-section">
      <div className="category-header">
        <h3 className="section-subtitle">違反フィルター設定</h3>
      </div>
      <div className="search-settings-content">
        <div className="search-toggle-section">
          <label className="search-toggle-label">
            <input
              type="checkbox"
              checked={metadata.enable_filtering ?? true}
              onChange={(e) => onChange({ ...metadata, enable_filtering: e.target.checked })}
            />
            <span>フィルタリングを有効化</span>
          </label>
        </div>
        {metadata.enable_filtering && (
          <div style={{ marginLeft: '20px', marginTop: '10px' }}>
            <div className="option-card search-keywords-card" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <label className="option-label">その他コンテンツのキーワード</label>
              <textarea
                className="option-textarea"
                placeholder="一行に1つのキーワードを入力"
                value={titleExtractionKeywordsText}
                onChange={(e) =>
                  onChange({ ...metadata, other_content_keywords: parseMultilineInput(e.target.value) })
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function GuidelineAutoExpandSettingsSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const { autoExpandKeywordsText } = useMetadataTexts(metadata);

  return (
    <div className="category-section">
      <div className="category-header">
        <h3 className="section-subtitle">自動展開設定</h3>
      </div>
      <div className="search-settings-content">
        <div style={{ marginLeft: '20px', marginTop: '10px' }}>
          <div className="option-card search-keywords-card" style={{ marginTop: '8px', marginBottom: '8px' }}>
            <label className="option-label">自動展開クリック条件キーワード</label>
            <textarea
              className="option-textarea"
              placeholder="一行に1つのキーワードを入力（ページ本文に含まれる場合のみ「もっと見る」等を自動クリック）"
              value={autoExpandKeywordsText}
              onChange={(e) =>
                onChange({ ...metadata, auto_expand_keywords: parseMultilineInput(e.target.value) })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function GuidelineUnfixableSettingsSection({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  const [unfixableUploadError, setUnfixableUploadError] = useState<string | null>(null);
  const [pendingUnfixableFile, setPendingUnfixableFile] = useState<File | null>(null);

  return (
    <UnfixableRulesSection
      enableUnfixableClassification={metadata.enable_unfixable_classification ?? false}
      onEnableUnfixableClassificationChange={(value) =>
        onChange({ ...metadata, enable_unfixable_classification: value })
      }
      unfixableByDomain={metadata.unfixable_by_domain ?? []}
      onUnfixableByDomainChange={(rules: UnfixableDomainRule[]) =>
        onChange({ ...metadata, unfixable_by_domain: sortUnfixableRules(rules) })
      }
      unfixableUploadError={unfixableUploadError}
      onUnfixableUploadErrorChange={setUnfixableUploadError}
      pendingUnfixableFile={pendingUnfixableFile}
      onPendingUnfixableFileChange={setPendingUnfixableFile}
      onDownload={() => {
        window.alert(
          'チューニングドラフトでは Excel ダウンロードは利用できません。ガイドライン管理画面をご利用ください。'
        );
      }}
      downloadDisabled={(metadata.unfixable_by_domain?.length ?? 0) === 0}
      onDirty={() => undefined}
    />
  );
}

/** All metadata sections in guideline-management screen order (without 違反カテゴリ). */
export function GuidelineMetadataForm({
  metadata,
  onChange,
}: {
  metadata: GuidelineDraftMetadata;
  onChange: (metadata: GuidelineDraftMetadata) => void;
}) {
  return (
    <>
      <GuidelineOrganizationSection metadata={metadata} onChange={onChange} />
      <GuidelineChunkSettingsSection metadata={metadata} onChange={onChange} />
      <GuidelineSearchSettingsSection metadata={metadata} onChange={onChange} />
      <GuidelineFilterSettingsSection metadata={metadata} onChange={onChange} />
      <GuidelineAutoExpandSettingsSection metadata={metadata} onChange={onChange} />
      <GuidelineUnfixableSettingsSection metadata={metadata} onChange={onChange} />
    </>
  );
}
